import React from "react";
import {
  Dialog,
  Box,
  Typography,
  Button,
  IconButton,
  Chip,
  Slide,
} from "@mui/material";
import {
  Close as CloseIcon,
  CheckCircleOutline as CheckIcon,
  WorkOutlineOutlined as WorkIcon,
  MonetizationOnOutlined as PayIcon,
  CategoryOutlined as AreaIcon,
  EmojiEventsOutlined as TrophyIcon,
  AssignmentOutlined as ReqIcon,
  ArrowForward as ApplyIcon,
} from "@mui/icons-material";

const Transition = React.forwardRef(function Transition(props, ref) {
  return <Slide direction="up" ref={ref} {...props} />;
});

const TIPO_CONFIG = {
  tiempo_completo: {
    label: "Tiempo completo",
    emoji: "⏰",
    color: "#00e676",
    bg: "rgba(0,230,118,0.10)",
  },
  medio_tiempo: {
    label: "Medio tiempo",
    emoji: "🕐",
    color: "#42a5f5",
    bg: "rgba(66,165,245,0.10)",
  },
  freelance: {
    label: "Freelance",
    emoji: "💼",
    color: "#ffd740",
    bg: "rgba(255,215,64,0.10)",
  },
  por_evento: {
    label: "Pago por evento",
    emoji: "🎪",
    color: "#ff9100",
    bg: "rgba(255,145,0,0.10)",
  },
};

/** Convierte texto con saltos/puntos/bullets en arreglo de strings */
const parseLines = (text) =>
  text
    ? text
        .split(/[\n;•|]/)
        .map((s) => s.replace(/^[-–*]\s*/, "").trim())
        .filter(Boolean)
    : [];

/** Lista de ítems con checkmark */
const BulletList = ({ items, color = "#00C9FF" }) => (
  <Box sx={{ display: "flex", flexDirection: "column", gap: 0.9 }}>
    {items.map((item, i) => (
      <Box key={i} sx={{ display: "flex", alignItems: "flex-start", gap: 1 }}>
        <CheckIcon
          sx={{ color, fontSize: 16, mt: 0.25, flexShrink: 0, opacity: 0.9 }}
        />
        <Typography
          sx={{
            fontSize: "0.85rem",
            color: "rgba(255,255,255,0.78)",
            lineHeight: 1.6,
          }}
        >
          {item}
        </Typography>
      </Box>
    ))}
  </Box>
);

/** Tarjeta de sección con borde de color */
const SectionCard = ({ icon, title, color, children }) => (
  <Box
    sx={{
      background: `${color}0a`,
      border: `1px solid ${color}25`,
      borderRadius: "16px",
      p: { xs: 2, sm: 2.5 },
      mb: 2,
    }}
  >
    <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1.5 }}>
      <Box
        sx={{
          color,
          display: "flex",
          alignItems: "center",
          background: `${color}18`,
          borderRadius: "8px",
          p: 0.6,
        }}
      >
        {icon}
      </Box>
      <Typography
        sx={{
          fontWeight: 700,
          fontSize: "0.88rem",
          color,
          letterSpacing: "0.02em",
        }}
      >
        {title}
      </Typography>
    </Box>
    {children}
  </Box>
);

/**
 * PuestoDetalleModal
 * Props:
 *   open       – boolean
 *   onClose    – fn
 *   vacante    – { titulo, area, tipo, descripcion, requisitos, beneficios }
 *   onPostular – fn opcional (callback "¡Quiero postularme!")
 */
const PuestoDetalleModal = ({ open, onClose, vacante, onPostular }) => {
  if (!vacante) return null;

  const tipoConf = TIPO_CONFIG[vacante.tipo] || TIPO_CONFIG.por_evento;
  const requisitos = parseLines(vacante.requisitos);
  const beneficios = parseLines(vacante.beneficios);

  const handlePostular = () => {
    onClose();
    if (onPostular) onPostular();
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      TransitionComponent={Transition}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: {
          background: "transparent",
          boxShadow: "none",
          overflow: "visible",
          m: { xs: 1, sm: 3 },
        },
      }}
      slotProps={{
        backdrop: {
          sx: {
            background: "rgba(4,6,20,0.88)",
            backdropFilter: "blur(18px)",
          },
        },
      }}
      sx={{ zIndex: 1400 }}
    >
      <style>{`
        @keyframes puestoFadeUp {
          from { opacity: 0; transform: translateY(18px); }
          to   { opacity: 1; transform: translateY(0); }
        }
        @keyframes gradientFlow {
          0%   { background-position: 0% 50%; }
          50%  { background-position: 100% 50%; }
          100% { background-position: 0% 50%; }
        }
        .puesto-body { animation: puestoFadeUp 0.38s ease forwards; }
      `}</style>

      <Box
        sx={{
          position: "relative",
          borderRadius: "24px",
          overflow: "hidden",
          background:
            "linear-gradient(145deg, #07091b 0%, #0c1225 55%, #08101e 100%)",
          border: "1px solid rgba(0,201,255,0.22)",
          boxShadow:
            "0 0 0 1px rgba(146,254,157,0.05), 0 32px 80px rgba(0,0,0,0.82), 0 0 70px rgba(0,201,255,0.09)",
          maxHeight: "92vh",
          display: "flex",
          flexDirection: "column",
        }}
      >
        {/* ── Barra superior animada ── */}
        <Box
          sx={{
            height: 4,
            background:
              "linear-gradient(90deg, #00C9FF, #92FE9D, #8250ff, #ff9100, #00C9FF)",
            backgroundSize: "300% 100%",
            animation: "gradientFlow 5s linear infinite",
            flexShrink: 0,
          }}
        />

        {/* ── HEADER ── */}
        <Box
          sx={{
            position: "relative",
            background:
              "linear-gradient(135deg, rgba(0,201,255,0.16) 0%, rgba(146,254,157,0.08) 50%, rgba(130,80,255,0.12) 100%)",
            borderBottom: "1px solid rgba(255,255,255,0.07)",
            px: { xs: 3, sm: 4 },
            pt: 3,
            pb: 2.5,
            flexShrink: 0,
          }}
        >
          {/* Orbe decorativo */}
          <Box
            sx={{
              position: "absolute",
              top: -40,
              right: -40,
              width: 200,
              height: 200,
              borderRadius: "50%",
              background:
                "radial-gradient(circle, rgba(0,201,255,0.12) 0%, transparent 70%)",
              pointerEvents: "none",
            }}
          />

          {/* Botón cerrar */}
          <IconButton
            onClick={onClose}
            size="small"
            sx={{
              position: "absolute",
              top: 12,
              right: 12,
              zIndex: 10,
              color: "rgba(255,255,255,0.45)",
              background: "rgba(255,255,255,0.06)",
              border: "1px solid rgba(255,255,255,0.1)",
              "&:hover": {
                color: "#fff",
                background: "rgba(255,255,255,0.12)",
              },
            }}
          >
            <CloseIcon fontSize="small" />
          </IconButton>

          {/* Etiqueta */}
          <Chip
            label="DESCRIPCIÓN DEL PUESTO"
            size="small"
            sx={{
              mb: 1.5,
              background: "rgba(0,201,255,0.1)",
              border: "1px solid rgba(0,201,255,0.3)",
              color: "#00C9FF",
              fontWeight: 700,
              letterSpacing: "0.08em",
              fontSize: "0.58rem",
            }}
          />

          {/* Título */}
          <Typography
            variant="h5"
            sx={{
              fontWeight: 800,
              fontSize: { xs: "1.25rem", sm: "1.45rem" },
              background: "linear-gradient(135deg, #fff 20%, rgba(0,201,255,0.85))",
              WebkitBackgroundClip: "text",
              WebkitTextFillColor: "transparent",
              backgroundClip: "text",
              lineHeight: 1.2,
              mb: 1.5,
              pr: 4,
            }}
          >
            {vacante.titulo}
          </Typography>

          {/* Tags */}
          <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1 }}>
            <Chip
              label={`${tipoConf.emoji} ${tipoConf.label}`}
              size="small"
              sx={{
                background: tipoConf.bg,
                border: `1px solid ${tipoConf.color}45`,
                color: tipoConf.color,
                fontWeight: 700,
                fontSize: "0.72rem",
              }}
            />
            {vacante.area && (
              <Chip
                icon={
                  <AreaIcon
                    sx={{
                      fontSize: "14px !important",
                      color: "rgba(146,254,157,0.7) !important",
                    }}
                  />
                }
                label={vacante.area}
                size="small"
                sx={{
                  background: "rgba(146,254,157,0.08)",
                  border: "1px solid rgba(146,254,157,0.22)",
                  color: "#92FE9D",
                  fontWeight: 600,
                  fontSize: "0.72rem",
                }}
              />
            )}
          </Box>
        </Box>

        {/* ── BODY scrollable ── */}
        <Box
          className="puesto-body"
          sx={{
            overflowY: "auto",
            flex: 1,
            px: { xs: 3, sm: 4 },
            py: 3,
            "&::-webkit-scrollbar": { width: 4 },
            "&::-webkit-scrollbar-track": {
              background: "rgba(255,255,255,0.03)",
            },
            "&::-webkit-scrollbar-thumb": {
              background: "rgba(0,201,255,0.25)",
              borderRadius: 2,
            },
          }}
        >
          {/* Descripción */}
          {vacante.descripcion && (
            <SectionCard
              icon={<WorkIcon sx={{ fontSize: 18 }} />}
              title="¿De qué trata este puesto?"
              color="#00C9FF"
            >
              <Typography
                sx={{
                  fontSize: "0.86rem",
                  color: "rgba(255,255,255,0.75)",
                  lineHeight: 1.7,
                  whiteSpace: "pre-line",
                }}
              >
                {vacante.descripcion}
              </Typography>
            </SectionCard>
          )}

          {/* Requisitos */}
          {requisitos.length > 0 && (
            <SectionCard
              icon={<ReqIcon sx={{ fontSize: 18 }} />}
              title="¿Qué necesitas para aplicar?"
              color="#ffd740"
            >
              <BulletList items={requisitos} color="#ffd740" />
            </SectionCard>
          )}

          {/* Beneficios */}
          {beneficios.length > 0 && (
            <SectionCard
              icon={<TrophyIcon sx={{ fontSize: 18 }} />}
              title="¿Qué te ofrecemos?"
              color="#92FE9D"
            >
              <BulletList items={beneficios} color="#92FE9D" />
            </SectionCard>
          )}

          {/* Esquema de pago */}
          <Box
            sx={{
              background: tipoConf.bg,
              border: `1px solid ${tipoConf.color}28`,
              borderRadius: "14px",
              p: 2,
              mb: 1,
              display: "flex",
              alignItems: "center",
              gap: 1.5,
            }}
          >
            <PayIcon sx={{ color: tipoConf.color, fontSize: 26, flexShrink: 0 }} />
            <Box>
              <Typography
                sx={{
                  fontSize: "0.68rem",
                  color: "rgba(255,255,255,0.38)",
                  fontWeight: 700,
                  letterSpacing: "0.07em",
                  textTransform: "uppercase",
                }}
              >
                Esquema de pago
              </Typography>
              <Typography
                sx={{
                  fontSize: "0.9rem",
                  color: tipoConf.color,
                  fontWeight: 700,
                }}
              >
                {tipoConf.label}
              </Typography>
            </Box>
          </Box>
        </Box>

        {/* ── FOOTER ── */}
        <Box
          sx={{
            px: { xs: 3, sm: 4 },
            pb: 3,
            pt: 2,
            borderTop: "1px solid rgba(255,255,255,0.06)",
            display: "flex",
            gap: 1.5,
            flexShrink: 0,
          }}
        >
          <Button
            onClick={onClose}
            fullWidth
            variant="outlined"
            sx={{
              borderRadius: "12px",
              borderColor: "rgba(255,255,255,0.14)",
              color: "rgba(255,255,255,0.5)",
              textTransform: "none",
              fontWeight: 600,
              py: 1.2,
              "&:hover": {
                borderColor: "rgba(255,255,255,0.28)",
                background: "rgba(255,255,255,0.04)",
              },
            }}
          >
            Cerrar
          </Button>
          <Button
            onClick={handlePostular}
            fullWidth
            sx={{
              borderRadius: "12px",
              background: "linear-gradient(90deg, #00C9FF, #92FE9D)",
              color: "#060d1a",
              fontWeight: 700,
              textTransform: "none",
              py: 1.2,
              "&:hover": {
                background: "linear-gradient(90deg, #92FE9D, #00C9FF)",
                transform: "translateY(-1px)",
                boxShadow: "0 8px 24px rgba(0,201,255,0.3)",
              },
              transition: "all 0.2s ease",
            }}
            endIcon={<ApplyIcon />}
          >
            ¡Quiero postularme!
          </Button>
        </Box>
      </Box>
    </Dialog>
  );
};

export default PuestoDetalleModal;
